import { Link, useParams } from 'react-router-dom'
import { useUser, useUpdateUser } from '../api/admin-api'
import { useAuthStore } from '@/stores/authStore'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'

export function UserDetail() {
  const { id } = useParams()
  const currentUser = useAuthStore((state) => state.user)
  const { data: user, isLoading, error } = useUser(id!)
  const updateMutation = useUpdateUser(id!)

  const handleToggleAdmin = async (checked: boolean) => {
    // 자기 자신의 관리자 권한을 해제하려는 경우 확인
    if (!checked && currentUser?.id === user?.id) {
      const confirmed = confirm(
        '자신의 관리자 권한을 해제하면 더 이상 관리자 페이지에 접근할 수 없습니다.\n\n정말 해제하시겠습니까?'
      )
      if (!confirmed) return
    }

    try {
      await updateMutation.mutateAsync({ isAdmin: checked })
      toast.success(checked ? '관리자 권한을 부여했습니다' : '관리자 권한을 해제했습니다')
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '권한 변경 중 오류가 발생했습니다')
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  if (!user) return null

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  return (
    <div className="max-w-4xl space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin">어드민</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/users">사용자</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>상세</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-heading tracking-tight">{user.email}</h1>
        <p className="text-caption mt-2 leading-relaxed">사용자 상세 정보</p>
      </div>

      {/* Main Info */}
      <div className="bg-white rounded-lg border p-6 space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700">ID</label>
            <div className="mt-1 font-mono text-sm text-gray-900">{user.id}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">이메일</label>
            <div className="mt-1 text-gray-900">{user.email}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">소스</label>
            <div className="mt-1">
              <Badge variant="outline">{user.source}</Badge>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">사용량 여부</label>
            <div className="mt-1">
              <Badge variant={user.hasUsage ? 'success' : 'secondary'}>
                {user.hasUsage ? '있음' : '없음'}
              </Badge>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">기업 ID</label>
            <div className="mt-1 text-gray-900">{user.enterpriseId ?? '—'}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">워크스페이스 ID</label>
            <div className="mt-1 text-gray-900">{user.workspaceId ?? '—'}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">웍스 역할</label>
            <div className="mt-1 text-gray-900">{user.role ?? '—'}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">가입일</label>
            <div className="mt-1 text-gray-900">{formatDate(user.createdAt)}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">수정일</label>
            <div className="mt-1 text-gray-900">
              {user.updatedAt ? formatDate(user.updatedAt) : '-'}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">토큰 만료시간</label>
            <div className="mt-1 text-gray-900">{formatDate(user.tokenExpiresAt)}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">인증 방식</label>
            <div className="mt-1">
              <Badge variant={user.authMode === 'oauth' ? 'default' : 'secondary'}>
                {user.authMode === 'oauth' ? '웍스 OAuth' : '레거시 wrksToken'}
              </Badge>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">리프레시 토큰 만료</label>
            <div className="mt-1 text-gray-900">
              {user.wrksRefreshExpiresAt ? formatDate(user.wrksRefreshExpiresAt) : '—'}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">웍스 sub</label>
            <div className="mt-1 text-gray-900 font-mono text-sm break-all">{user.wrksSub ?? '—'}</div>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">SSO 토큰 (마스킹)</label>
          <div className="mt-1 p-4 bg-gray-50 rounded-lg text-gray-900 font-mono text-sm break-all">
            {user.ssoTokenMasked ?? '—'}
          </div>
        </div>

        {/* Admin Toggle */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div>
            <Label htmlFor="admin-toggle" className="text-base font-medium">
              관리자 권한
            </Label>
            <p className="text-sm text-gray-500 mt-1">
              관리자는 백오피스에 접근할 수 있습니다
            </p>
          </div>
          <Switch
            id="admin-toggle"
            checked={user.isAdmin}
            onCheckedChange={handleToggleAdmin}
            disabled={updateMutation.isPending}
          />
        </div>
      </div>
    </div>
  )
}
